import { useCallback, useEffect, useMemo } from 'react';

import moment from 'moment-timezone';
import { WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useProgramCriticalAnalysisStore } from 'store/programCriticalAnalysis.store';

import {
    SelectByOptionsValue,
    resolutionOptions,
    selectByOptions,
    serializeHeatmap,
} from 'components/widgets/RiskDensity/RiskDensity.utils';

export const useRiskDensityLogic = ({
    ui,
    api,
    setDateRange,
    setDateRangeOption,
    setEndProjectDate,
    setStartProjectDate,
    setRiskDensityBy,
    setResolution,
    setSelectBy,
    widgetViewMode,
}) => {
    const { update } = useProgramCriticalAnalysisStore();

    const serialized = useMemo(() => {
        return serializeHeatmap(api.riskDensityBy.instance.data?.heatMapItemResponseList, ui.state.resolution);
    }, [api.riskDensityBy.instance.data?.heatMapItemResponseList, ui.state.resolution]);

    // handlers
    const handleSelectBy = (value: ISelectOption<SelectByOptionsValue> | null) => {
        if (value) {
            setRiskDensityBy(value.value);
            setSelectBy(value.value);
        }
    };

    const handleDateRangeSelection = (value: { startDate: number | null; endDate: number | null }) => {
        setDateRange(value);
    };

    const handleResolutionFilter = (value: ISelectOption<boolean>) => {
        setResolution(value.value);
    };

    const setDateOnDelayDriver = useCallback(
        (value: string) => {
            const date = serialized.find((item) => item.id === value);
            if (date) {
                const m = moment(date.date);
                update({
                    dateFilter: {
                        value: m.format('yyyyMM'),
                        label: m.format('MMM yyyy'),
                    },
                });
            }
        },
        [serialized],
    );

    const selectByValue = selectByOptions.find((item) => item.value === ui.state.selectBy);
    const resolutionValue = resolutionOptions.find((item) => item.value === ui.state.resolution);

    //useEffects

    // reset Date range whenever one of the global filters has been changed.
    useEffect(() => {
        if (!WidgetViewModeUtils.isReportEditWidget(widgetViewMode)) {
            if (!ui.inPinboard) {
                setDateRange({
                    startDate: null,
                    endDate: null,
                });
                setDateRangeOption('wholeProject');
                setEndProjectDate(null);
                setStartProjectDate(null);
            }
        }
    }, [ui.inPinboard, ui.state.activityCodes, ui.state.milestoneFragnet, ui.state.tags, ui.state.wbs]);

    useEffect(() => {
        if (api.riskDensityBy.instance.data && !ui.state.endProjectDate && !ui.state.startProjectDate) {
            setEndProjectDate(api.riskDensityBy.instance.data?.endProjectDate);
            setStartProjectDate(api.riskDensityBy.instance.data?.startProjectDate);
        }
    }, [
        ui.state.endProjectDate,
        ui.state.startProjectDate,
        api.riskDensityBy.instance.data,
        api.riskDensityBy.instance.data?.endProjectDate,
        api.riskDensityBy.instance.data?.startProjectDate,
    ]);

    return {
        logic: {
            handleSelectBy,
            handleDateRangeSelection,
            handleResolutionFilter,
            serialized,
            setDateOnDelayDriver,
            selectByValue,
            resolutionValue,
        },
    };
};
